import { AlertTriangle, ArrowDownUp, BookOpen, MapPinned } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { Badge } from '@/components/admin/shared/badge'
import { Textarea } from '@/components/ui/textarea'
import type { EloFit } from '@/lib/recommendation/elo-targeting.js'
import { trpc } from '@/lib/trpc'
import { useRecommendationEngineMock } from './recommendation-engine-context'
import { RecommendationEngineNoData } from './recommendation-engine-data-placeholders'

const EMPTY_CLIENT_ID = '00000000-0000-0000-0000-000000000000'
const DEFAULT_MANUAL_MIN_OFFSET = 0
const DEFAULT_MANUAL_MAX_OFFSET = 500
const PRELOADED_PROBLEMS_PER_SUBTOPIC = 100
const INITIAL_RECOMMENDATIONS = 2
const EXPANSION_STEP = 5
const ELO_OFFSET_DEBOUNCE_MS = 400

function formatGraphCoordinate(value: number): string {
  return value.toFixed(2)
}

function clampOffset(value: number): number {
  return Math.max(0, Math.min(2000, value))
}

function parseOffsetInput(value: string, fallback: number): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return clampOffset(Math.round(parsed))
}

function formatEloValue(value: number | null): string {
  if (value === null) return 'Unrated'
  return String(Math.round(value))
}

function deltaClassName(fit: EloFit): string {
  switch (fit) {
    case 'below':
      return 'text-blue-600 dark:text-blue-400'
    case 'above':
      return 'text-rose-600 dark:text-rose-400'
    default:
      return 'text-foreground'
  }
}

type RecommendationBucketEntry = {
  problemId: string
  problemTitle: string
  difficulty: string
  eloRating: number | null
  eloDelta: number | null
  eloFit: EloFit
  weight: 'primary' | 'secondary'
}

type ProblemExpansionState = {
  top: number
  bottom: number
}

function deriveProblemDisplayBuckets<T extends RecommendationBucketEntry>(
  allProblems: T[],
): {
  initial: T[]
  topPool: T[]
  bottomPool: T[]
} {
  const inRange = allProblems.filter((problem) => problem.eloFit === 'in_range')
  const below = allProblems.filter((problem) => problem.eloFit === 'below')
  const above = allProblems.filter((problem) => problem.eloFit === 'above')
  const unrated = allProblems.filter((problem) => problem.eloFit === 'unrated')
  const outside = allProblems.filter((problem) => problem.eloFit !== 'in_range')

  const initial =
    inRange.length > 0
      ? [
          ...inRange.slice(0, INITIAL_RECOMMENDATIONS),
          ...outside.slice(
            0,
            Math.max(0, INITIAL_RECOMMENDATIONS - inRange.length),
          ),
        ]
      : outside.slice(0, INITIAL_RECOMMENDATIONS)

  const initialIds = new Set(initial.map((problem) => problem.problemId))
  const topPool = below.filter((problem) => !initialIds.has(problem.problemId))
  const bottomPool = [
    ...inRange.filter((problem) => !initialIds.has(problem.problemId)),
    ...above.filter((problem) => !initialIds.has(problem.problemId)),
    ...unrated.filter((problem) => !initialIds.has(problem.problemId)),
  ]

  return { initial, topPool, bottomPool }
}

type GraphViewProps = {
  clientId?: string
}

export function GraphView({ clientId }: GraphViewProps) {
  const [subtopicFilter, setSubtopicFilter] = useState('')
  const [sortDirection, setSortDirection] = useState<
    'ascending' | 'descending'
  >('ascending')
  const { saveGraphJsonEditorState } = useRecommendationEngineMock()
  const [editorText, setEditorText] = useState('')
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [editorSaveState, setEditorSaveState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle')
  const [manualMinOffsetInput, setManualMinOffsetInput] = useState(
    String(DEFAULT_MANUAL_MIN_OFFSET),
  )
  const [manualMaxOffsetInput, setManualMaxOffsetInput] = useState(
    String(DEFAULT_MANUAL_MAX_OFFSET),
  )
  const [manualMinOffset, setManualMinOffset] = useState(
    DEFAULT_MANUAL_MIN_OFFSET,
  )
  const [manualMaxOffset, setManualMaxOffset] = useState(
    DEFAULT_MANUAL_MAX_OFFSET,
  )
  const [problemExpansionBySubtopicId, setProblemExpansionBySubtopicId] =
    useState<Record<string, ProblemExpansionState>>({})

  const graphQuery =
    trpc.admin.recommendationEngine.getGraphRecommendationsState.useQuery(
      {
        clientId: clientId ?? EMPTY_CLIENT_ID,
        subtopicId: subtopicFilter || undefined,
        sortDirection,
        rowsLimit: 200,
        problemsPerSubtopic: PRELOADED_PROBLEMS_PER_SUBTOPIC,
        manualEloRangeOffsets: {
          minOffset: manualMinOffset,
          maxOffset: manualMaxOffset,
        },
      },
      { enabled: Boolean(clientId) },
    )
  const graphEditorStateQuery =
    trpc.admin.recommendationEngine.getGraphJsonEditorState.useQuery(
      {
        clientId: clientId ?? EMPTY_CLIENT_ID,
      },
      {
        enabled: Boolean(clientId),
      },
    )

  const editorPayload = graphEditorStateQuery.data?.payload
  const canonicalEditorText = useMemo(
    () => (editorPayload ? JSON.stringify(editorPayload, null, 2) : ''),
    [editorPayload],
  )
  useEffect(() => {
    if (
      !editorDirty &&
      editorText !== canonicalEditorText &&
      canonicalEditorText
    ) {
      setEditorText(canonicalEditorText)
    }
  }, [canonicalEditorText, editorDirty, editorText])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextMinOffset = parseOffsetInput(
        manualMinOffsetInput,
        DEFAULT_MANUAL_MIN_OFFSET,
      )
      const nextMaxOffset = Math.max(
        parseOffsetInput(manualMaxOffsetInput, DEFAULT_MANUAL_MAX_OFFSET),
        nextMinOffset,
      )

      setManualMinOffset(nextMinOffset)
      setManualMaxOffset(nextMaxOffset)

      const nextMinInput = String(nextMinOffset)
      const nextMaxInput = String(nextMaxOffset)
      if (manualMinOffsetInput !== nextMinInput) {
        setManualMinOffsetInput(nextMinInput)
      }
      if (manualMaxOffsetInput !== nextMaxInput) {
        setManualMaxOffsetInput(nextMaxInput)
      }
    }, ELO_OFFSET_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [manualMinOffsetInput, manualMaxOffsetInput])

  if (!clientId) {
    return (
      <RecommendationEngineNoData message="GRAPH tab requires a client context." />
    )
  }

  if (graphQuery.isPending) {
    return (
      <RecommendationEngineNoData message="Loading graph recommendations..." />
    )
  }

  if (graphQuery.isError || !graphQuery.data) {
    return (
      <RecommendationEngineNoData message="Could not load graph recommendations." />
    )
  }

  const { rows, subtopicOptions } = graphQuery.data

  const updateExpansion = (
    subtopicId: string,
    direction: 'top' | 'bottom',
    maxValue: number,
  ) => {
    setProblemExpansionBySubtopicId((previous) => {
      const current = previous[subtopicId] ?? { top: 0, bottom: 0 }
      const nextValue = Math.min(maxValue, current[direction] + EXPANSION_STEP)
      if (nextValue === current[direction]) return previous
      return {
        ...previous,
        [subtopicId]: {
          ...current,
          [direction]: nextValue,
        },
      }
    })
  }

  const handleSaveEditor = async () => {
    setEditorError(null)
    setEditorSaveState('idle')
    let parsed: {
      adjacencyList: Record<string, string[]>
      meta: Record<
        string,
        {
          niche: number
          learning_order: number
        }
      >
    }
    try {
      parsed = JSON.parse(editorText) as typeof parsed
    } catch {
      setEditorError('Invalid JSON format.')
      setEditorSaveState('error')
      return
    }
    if (!saveGraphJsonEditorState) {
      setEditorError('Graph editor save action is unavailable.')
      setEditorSaveState('error')
      return
    }
    setEditorSaveState('saving')
    try {
      await saveGraphJsonEditorState(parsed)
      setEditorDirty(false)
      setEditorSaveState('saved')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save graph JSON.'
      setEditorError(message)
      setEditorSaveState('error')
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="order-2 space-y-4 lg:order-1 lg:col-span-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <MapPinned size={14} />
            Foundational to niche + prerequisite-aware ordering
          </div>
          <select
            value={subtopicFilter}
            onChange={(event) => setSubtopicFilter(event.target.value)}
            className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1.5 text-sm"
            aria-label="Filter by subtopic"
          >
            <option value="">All subtopics</option>
            {subtopicOptions.map((subtopic) => (
              <option key={subtopic.id} value={subtopic.id}>
                {subtopic.topicName}: {subtopic.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() =>
              setSortDirection((previous) =>
                previous === 'ascending' ? 'descending' : 'ascending',
              )
            }
            className="bg-muted border-border text-foreground inline-flex items-center rounded-lg border px-2.5 py-1.5 text-sm"
            aria-label={
              sortDirection === 'ascending'
                ? 'Sort recommendations ascending'
                : 'Sort recommendations descending'
            }
          >
            <ArrowDownUp size={14} />
          </button>
          <span className="text-muted-foreground px-1 text-xs font-medium">
            Global Elo offsets
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-muted-foreground inline-flex items-center gap-1 text-xs">
              Min offset
              <input
                type="number"
                min={0}
                max={2000}
                value={manualMinOffsetInput}
                onChange={(event) =>
                  setManualMinOffsetInput(event.target.value)
                }
                className="bg-muted border-border text-foreground w-20 rounded border px-2 py-1 text-xs"
                aria-label="Manual Elo minimum offset"
              />
            </label>
            <label className="text-muted-foreground inline-flex items-center gap-1 text-xs">
              Max offset
              <input
                type="number"
                min={0}
                max={2000}
                value={manualMaxOffsetInput}
                onChange={(event) =>
                  setManualMaxOffsetInput(event.target.value)
                }
                className="bg-muted border-border text-foreground w-20 rounded border px-2 py-1 text-xs"
                aria-label="Manual Elo maximum offset"
              />
            </label>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-muted-foreground border-border rounded-lg border border-dashed py-10 text-center text-sm">
            No graph recommendations available for the current filters.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => {
              const allProblems = row.recommendedProblems
              const { initial, topPool, bottomPool } =
                deriveProblemDisplayBuckets(allProblems)
              const expansion = problemExpansionBySubtopicId[
                row.subtopicId
              ] ?? {
                top: 0,
                bottom: 0,
              }
              const topVisible = topPool.slice(0, expansion.top)
              const bottomVisible = bottomPool.slice(0, expansion.bottom)
              const displayedProblems = [
                ...topVisible,
                ...initial,
                ...bottomVisible,
              ]
              const displayableProblemCount = Math.min(
                row.recommendedProblemCount,
                allProblems.length,
              )
              const hiddenTop = Math.max(0, topPool.length - expansion.top)
              const hiddenBottom = Math.max(
                0,
                bottomPool.length - expansion.bottom,
              )
              const showRecommendations =
                displayedProblems.length > 0 && displayableProblemCount > 0

              return (
                <div
                  key={row.subtopicId}
                  className="border-border rounded-lg border p-3"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-muted-foreground text-xs font-semibold">
                          #{row.rank}
                        </span>
                        <span className="text-foreground text-sm font-semibold">
                          {row.topicName}
                          {' -> '}
                          {row.subtopicName}
                        </span>
                        <Badge variant="topic">
                          ({formatGraphCoordinate(row.graphX)},{' '}
                          {formatGraphCoordinate(row.graphY)})
                        </Badge>
                        <Badge
                          variant={row.needsPractice ? 'premium' : 'active'}
                        >
                          {row.needsPractice ? 'Needs practice' : 'Mastered'}
                        </Badge>
                        <Badge
                          variant={
                            row.adjacencyRole === 'foundation'
                              ? 'active'
                              : row.adjacencyRole === 'bridge'
                                ? 'premium'
                                : row.adjacencyRole === 'leaf'
                                  ? 'paused'
                                  : 'default'
                          }
                        >
                          {row.adjacencyRole === 'foundation'
                            ? 'Foundation'
                            : row.adjacencyRole === 'bridge'
                              ? 'Bridge'
                              : row.adjacencyRole === 'leaf'
                                ? 'Leaf'
                                : 'Isolated'}
                        </Badge>
                        {row.unlockedByOwnMastery && (
                          <Badge variant="default">
                            Unlocked by own mastery
                          </Badge>
                        )}
                        {!row.directPrerequisitesMet && (
                          <Badge variant="paused">
                            Direct prerequisites unmet
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="bg-muted/30 border-border text-muted-foreground rounded-md border px-3 py-2 text-xs leading-5 lg:max-w-[28rem]">
                      Mastery {row.mastery.toFixed(1)} | min threshold{' '}
                      {row.minProficiencyThreshold} | max threshold{' '}
                      {row.maxProficiencyThreshold} | Elo target{' '}
                      {Math.round(row.targetElo)} | prerequisites:{' '}
                      {row.incomingPrerequisiteCount} | unlocks:{' '}
                      {row.dependentSubtopicCount}
                    </div>
                  </div>

                  {row.warnings.length > 0 && (
                    <div className="mt-3 space-y-1 rounded-md border border-amber-300/50 bg-amber-50/40 p-2 dark:border-amber-400/40 dark:bg-amber-950/20">
                      <p className="text-foreground flex items-center gap-1.5 text-xs font-medium">
                        <AlertTriangle size={13} className="text-amber-500" />
                        Prerequisite warnings
                      </p>
                      {row.warnings.map((warning) => (
                        <p
                          key={warning.subtopicId}
                          className="text-muted-foreground text-xs"
                        >
                          Current mastery of {warning.subtopicName} is{' '}
                          {warning.currentMastery.toFixed(1)} and
                          minProficiencyThreshold is{' '}
                          {warning.minProficiencyThreshold}. Path:{' '}
                          {warning.pathNames.join(' -> ')}.
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="mt-3">
                    <p className="text-foreground mb-1 flex items-center gap-1.5 text-xs font-medium">
                      <BookOpen size={13} />
                      Recommended problems ({displayedProblems.length}/
                      {displayableProblemCount})
                    </p>
                    {!showRecommendations ? (
                      <p className="text-muted-foreground text-xs">
                        No tagged problems are currently available for this
                        subtopic.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {hiddenTop > 0 && (
                          <button
                            type="button"
                            className="bg-muted border-border text-foreground rounded-md border px-2 py-1 text-xs"
                            onClick={() =>
                              updateExpansion(
                                row.subtopicId,
                                'top',
                                topPool.length,
                              )
                            }
                          >
                            Show {Math.min(EXPANSION_STEP, hiddenTop)} more
                            lower-range matches
                          </button>
                        )}
                        <div className="border-border overflow-x-auto rounded-md border">
                          <table className="w-full text-xs">
                            <thead className="bg-muted/40 text-muted-foreground">
                              <tr>
                                <th className="px-2 py-1 text-left font-medium">
                                  Problem
                                </th>
                                <th className="px-2 py-1 text-left font-medium">
                                  Difficulty
                                </th>
                                <th className="px-2 py-1 text-left font-medium">
                                  Weight
                                </th>
                                <th className="px-2 py-1 text-left font-medium">
                                  Elo
                                </th>
                                <th className="px-2 py-1 text-left font-medium">
                                  Δ
                                </th>
                                <th className="px-2 py-1 text-left font-medium">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayedProblems.map((problem) => (
                                <tr
                                  key={`${row.subtopicId}:${problem.problemId}`}
                                  className="border-border border-t"
                                >
                                  <td className="text-foreground px-2 py-1.5">
                                    {problem.problemTitle}
                                  </td>
                                  <td className="text-muted-foreground px-2 py-1.5">
                                    {problem.difficulty}
                                  </td>
                                  <td className="text-muted-foreground px-2 py-1.5 uppercase">
                                    {problem.weight}
                                  </td>
                                  <td className="text-muted-foreground px-2 py-1.5">
                                    {formatEloValue(problem.eloRating)}
                                  </td>
                                  <td
                                    className={`px-2 py-1.5 ${deltaClassName(problem.eloFit)}`}
                                  >
                                    {problem.eloDelta === null
                                      ? '—'
                                      : problem.eloDelta}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <button
                                      type="button"
                                      disabled
                                      className="bg-muted text-muted-foreground border-border rounded border px-2 py-0.5 text-[11px]"
                                      title="Homework staging integration coming soon"
                                    >
                                      Add to homework staging
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {hiddenBottom > 0 && (
                          <button
                            type="button"
                            className="bg-muted border-border text-foreground rounded-md border px-2 py-1 text-xs"
                            onClick={() =>
                              updateExpansion(
                                row.subtopicId,
                                'bottom',
                                bottomPool.length,
                              )
                            }
                          >
                            Show {Math.min(EXPANSION_STEP, hiddenBottom)} more
                            in/at/above-range matches
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="order-1 lg:order-2 lg:col-span-1">
        <div className="border-border bg-card rounded-lg border p-3 lg:sticky lg:top-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-foreground text-sm font-semibold">
              Graph JSON editor
            </p>
            <button
              type="button"
              className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1 text-xs"
              onClick={() => {
                setEditorText(canonicalEditorText)
                setEditorDirty(false)
                setEditorError(null)
                setEditorSaveState('idle')
              }}
            >
              Reset
            </button>
          </div>
          <Textarea
            value={editorText}
            onChange={(event) => {
              setEditorText(event.target.value)
              setEditorDirty(true)
              setEditorSaveState('idle')
            }}
            className="min-h-[24rem] font-mono text-xs"
            aria-label="Graph JSON editor"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-muted-foreground text-xs">
              {editorSaveState === 'saved'
                ? 'Saved globally. Recommendations refreshed.'
                : editorSaveState === 'saving'
                  ? 'Saving...'
                  : 'Edit adjacencyList/meta and save globally for all clients.'}
            </p>
            <button
              type="button"
              onClick={() => void handleSaveEditor()}
              disabled={editorSaveState === 'saving' || !editorDirty}
              className="bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground rounded-lg px-3 py-1.5 text-xs"
            >
              Save graph JSON
            </button>
          </div>
          {editorError && (
            <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
              {editorError}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
